'use client';

import React, { useState, useEffect } from 'react';
import AIModelSelector from './AIModelSelector';
import PromptArea from './PromptArea';
import GenerationProgressModal from './GenerationProgressModal';
import InsufficientCreditsModal from './InsufficientCreditsModal';
import CreditsPurchaseModal from '@/components/CreditsPurchaseModal';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

export type AIModelId = 'free' | 'pro' | 'advanced';

export interface AIModel {
  id: AIModelId;
  name: string;
  description: string;
  quality: string;
  creditsPerGeneration: number;
  accessType: string;
  badge: 'free' | 'pro' | 'advanced';
  features: string[];
  dbId?: string;
}

export const DEFAULT_AI_MODELS: AIModel[] = [
  {
    id: 'free',
    name: 'ForgeAI Free',
    description: 'Modelo gratuito ideal para experimentar e criar jogos simples. Perfeito para começar.',
    quality: 'Básica',
    creditsPerGeneration: 5,
    accessType: 'Gratuito',
    badge: 'free',
    features: ['Jogos 2D simples', 'Mecânicas básicas', 'Assets gerados por IA', 'Rápido (< 15s)'],
  },
  {
    id: 'pro',
    name: 'ForgeAI Pro',
    description: 'Modelo avançado para jogos mais complexos com mecânicas ricas e melhor geração de código.',
    quality: 'Alta',
    creditsPerGeneration: 15,
    accessType: 'Créditos Premium',
    badge: 'pro',
    features: ['Jogos 2D/3D complexos', 'Sistemas de progressão', 'IA de inimigos', 'Física avançada'],
  },
  {
    id: 'advanced',
    name: 'ForgeAI Advanced',
    description: 'O nosso modelo mais poderoso. Máxima qualidade para jogos profissionais e complexos.',
    quality: 'Máxima',
    creditsPerGeneration: 30,
    accessType: 'Créditos Premium',
    badge: 'advanced',
    features: ['Projetos complexos', 'Múltiplos níveis', 'Sistemas RPG completos', 'Qualidade profissional'],
  },
];

export default function CreateGameScreen() {
  const { user, credits, refreshCredits } = useAuth();
  const router = useRouter();
  const [selectedModel, setSelectedModel] = useState<AIModelId>('free');
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [aiModels, setAiModels] = useState<AIModel[]>(DEFAULT_AI_MODELS);
  const [createdGameId, setCreatedGameId] = useState<string | null>(null);

  // Load AI models from DB
  useEffect(() => {
    const loadModels = async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('ai_models')
          .select('*')
          .eq('is_active', true)
          .order('sort_order');
        if (data && data.length > 0) {
          const mapped: AIModel[] = data.map((m) => ({
            id: m.badge as AIModelId,
            name: m.name,
            description: m.description,
            quality: m.quality,
            creditsPerGeneration: m.credits_per_generation,
            accessType: m.access_type,
            badge: m.badge as 'free' | 'pro' | 'advanced',
            features: Array.isArray(m.features) ? m.features : [],
            dbId: m.id,
          }));
          setAiModels(mapped);
        }
      } catch {}
    };
    loadModels();
  }, []);

  const model = aiModels.find((m) => m.id === selectedModel) || aiModels[0];

  const dailyRemaining = (credits?.daily_credits_total ?? 75) - (credits?.daily_credits_used ?? 0);
  const purchasedCredits = credits?.purchased_credits ?? 0;
  const totalCredits = dailyRemaining + purchasedCredits;
  const canGenerate = totalCredits >= (model?.creditsPerGeneration || 5);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    if (!canGenerate) {
      setShowPurchaseModal(true);
      return;
    }

    // Fire DB operations in background — don't block generation on DB errors
    if (user) {
      (async () => {
        try {
          const supabase = createClient();
          await supabase.rpc('consume_credits', {
            p_user_id: user.id,
            p_amount: model.creditsPerGeneration,
            p_description: `Geração de jogo com ${model.name}`,
          });

          const { data: game } = await supabase
            .from('games')
            .insert({
              user_id: user.id,
              title: prompt.slice(0, 50) || 'Novo Jogo',
              game_status: 'draft',
              ai_model_id: model.dbId,
            })
            .select('id')
            .single();

          if (game) setCreatedGameId(game.id);

          await supabase.from('ai_generations').insert({
            user_id: user.id,
            game_id: game?.id,
            ai_model_id: model.dbId,
            prompt,
            credits_used: model.creditsPerGeneration,
            status: 'pending',
          });

          await refreshCredits();
        } catch (err) {
          console.warn('DB operations failed (non-blocking):', err);
        }
      })();
    }

    // Always start generation immediately
    setIsGenerating(true);
  };

  const handleGenerationComplete = (gameCode: string) => {
    setIsGenerating(false);
    if (gameCode) {
      sessionStorage.setItem('generatedGameCode', gameCode);
      sessionStorage.setItem('generatedGamePrompt', prompt);
    }
    window.location.href = '/game-editor';
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
            <Icon name="SparklesIcon" size={16} className="text-white" variant="solid" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Criar jogo</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Descreve o jogo que queres criar e a IA trata do resto.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <div className="xl:col-span-1 xl:order-2 order-1">
          <AIModelSelector
            models={aiModels}
            selectedModel={selectedModel}
            onSelect={setSelectedModel}
            dailyCredits={dailyRemaining}
            purchasedCredits={purchasedCredits}
          />
        </div>

        <div className="xl:col-span-2 xl:order-1 order-2 space-y-6">
          <PromptArea
            prompt={prompt}
            setPrompt={setPrompt}
            onGenerate={handleGenerate}
            model={model}
            canGenerate={canGenerate}
            dailyCredits={dailyRemaining}
            purchasedCredits={purchasedCredits}
          />
        </div>
      </div>

      {isGenerating && (
        <GenerationProgressModal
          prompt={prompt}
          modelName={model.name}
          onComplete={handleGenerationComplete}
          modelId={model.id}
        />
      )}

      {showInsufficientModal && (
        <InsufficientCreditsModal
          onClose={() => setShowInsufficientModal(false)}
          creditsNeeded={model.creditsPerGeneration}
          dailyCredits={dailyRemaining}
          purchasedCredits={purchasedCredits}
        />
      )}

      {showPurchaseModal && (
        <CreditsPurchaseModal
          onClose={() => setShowPurchaseModal(false)}
          onSuccess={() => {
            setShowPurchaseModal(false);
            refreshCredits();
          }}
        />
      )}
    </div>
  );
}